// src/components/vendor/VendorWelcomeModal.tsx
// Modal de Boas-vindas — aparece APENAS na primeira visita ao painel do vendedor

import React, { useEffect, useState } from 'react';
import { Ticket, Share2, Wallet, ArrowRight, X, Sparkles } from 'lucide-react';
import './VendorWelcomeModal.css';

const STORAGE_KEY = 'graia_vendor_welcomed';

interface VendorWelcomeModalProps {
    onClose: () => void;
    onCreateCoupon?: () => void;
}

const VendorWelcomeModal: React.FC<VendorWelcomeModalProps> = ({ onClose, onCreateCoupon }) => {
    const [visible, setVisible] = useState(false);
    const [closing, setClosing] = useState(false);

    useEffect(() => {
        // Pequeno delay para a animação de entrada ser suave
        const t = setTimeout(() => setVisible(true), 50);
        return () => clearTimeout(t);
    }, []);

    const handleClose = () => {
        setClosing(true);
        setTimeout(() => {
            localStorage.setItem(STORAGE_KEY, 'true');
            onClose();
        }, 380);
    };

    const handleCTA = () => {
        setClosing(true);
        setTimeout(() => {
            localStorage.setItem(STORAGE_KEY, 'true');
            onClose();
            onCreateCoupon?.();
        }, 380);
    };

    return (
        <div className={`vwm-overlay ${visible ? 'vwm-overlay--visible' : ''} ${closing ? 'vwm-overlay--closing' : ''}`}>
            <div className={`vwm-modal ${visible ? 'vwm-modal--visible' : ''} ${closing ? 'vwm-modal--closing' : ''}`}>

                {/* Botão fechar */}
                <button className="vwm-close" onClick={handleClose} aria-label="Fechar">
                    <X size={16} />
                </button>

                {/* Topo decorativo */}
                <div className="vwm-hero">
                    <div className="vwm-hero-glow" />
                    <div className="vwm-hero-icon-wrap">
                        <Sparkles size={28} className="vwm-hero-sparkle" />
                    </div>
                    <div className="vwm-badge">Acesso liberado</div>
                </div>

                {/* Corpo */}
                <div className="vwm-body">
                    <h1 className="vwm-title">
                        Você já pode <span className="vwm-title-accent">começar a vender.</span>
                    </h1>
                    <p className="vwm-subtitle">
                        Crie cupons, compartilhe seu link e receba comissão por cada venda aprovada.
                    </p>

                    {/* Passos */}
                    <div className="vwm-steps">
                        <div className="vwm-step">
                            <div className="vwm-step-icon vwm-step-icon--green">
                                <Ticket size={18} />
                            </div>
                            <div className="vwm-step-text">
                                <strong>Crie um cupom ou link</strong>
                                <span>Personalize o desconto e associe a um produto</span>
                            </div>
                        </div>

                        <div className="vwm-step-connector">
                            <ArrowRight size={14} />
                        </div>

                        <div className="vwm-step">
                            <div className="vwm-step-icon vwm-step-icon--blue">
                                <Share2 size={18} />
                            </div>
                            <div className="vwm-step-text">
                                <strong>Compartilhe com sua audiência</strong>
                                <span>Redes sociais, stories, grupos — onde quiser</span>
                            </div>
                        </div>

                        <div className="vwm-step-connector">
                            <ArrowRight size={14} />
                        </div>

                        <div className="vwm-step">
                            <div className="vwm-step-icon vwm-step-icon--emerald">
                                <Wallet size={18} />
                            </div>
                            <div className="vwm-step-text">
                                <strong>Receba sua comissão</strong>
                                <span>Comissões aprovadas ficam disponíveis em 7 dias</span>
                            </div>
                        </div>
                    </div>

                    {/* CTA */}
                    <button className="vwm-cta" onClick={handleCTA}>
                        <Ticket size={18} />
                        Criar meu primeiro cupom
                        <ArrowRight size={16} className="vwm-cta-arrow" />
                    </button>

                    {/* Microcopy */}
                    <p className="vwm-microcopy">
                        Acompanhe todas as suas vendas e comissões em tempo real no painel.
                    </p>
                </div>
            </div>
        </div>
    );
};

/**
 * Hook utilitário — verifica se o modal deve ser exibido
 * Use assim no VendorDashboard:
 *   const { showWelcome, dismissWelcome } = useVendorWelcome();
 */
export function useVendorWelcome() {
    const [showWelcome, setShowWelcome] = useState(false);

    useEffect(() => {
        const alreadySeen = localStorage.getItem(STORAGE_KEY);
        if (!alreadySeen) {
            // Pequeno delay para o dashboard carregar antes de abrir o modal
            const t = setTimeout(() => setShowWelcome(true), 600);
            return () => clearTimeout(t);
        }
    }, []);

    const dismissWelcome = () => setShowWelcome(false);

    return { showWelcome, dismissWelcome };
}

export default VendorWelcomeModal;